/**
 * <side-drawer> — A general-purpose slide-in drawer built on native <dialog>.
 *
 * Attributes:
 *   side   — "left" (default) or "right"
 *   theme  — "light" | "dark" | omitted (inherits page theme)
 *
 * Public API:
 *   drawer.showModal()  — open
 *   drawer.close()      — close
 *
 * Trigger from anywhere:
 *   <button data-open-drawer="drawer-id">Open</button>
 */
class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this._isOpen = false;
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onDialogCancel = this._onDialogCancel.bind(this);
    this._onTransitionEnd = this._onTransitionEnd.bind(this);
  }

  connectedCallback() {
    if (this._built) return;
    this._built = true;

    const side = this.getAttribute('side') || 'left';
    const theme = this.getAttribute('theme'); // "light" | "dark" | null

    // Capture original content before we restructure
    const originalContent = this.innerHTML;
    // Use Element.prototype to bypass our custom setter (not built yet)
    Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML').set.call(this, '');

    // Inject ::backdrop rule once (pseudo-elements can't be styled inline)
    if (!document.getElementById('side-drawer-styles')) {
      const style = document.createElement('style');
      style.id = 'side-drawer-styles';
      style.textContent = 'side-drawer dialog::backdrop { background: transparent; }';
      document.head.appendChild(style);
    }

    // Build the dialog shell — use inline styles for layout
    const dialog = document.createElement('dialog');
    Object.assign(dialog.style, {
      position: 'fixed', inset: '0', margin: '0', padding: '0',
      width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none',
      background: 'transparent', border: 'none', outline: 'none',
      overflow: 'hidden',
    });

    // Backdrop (click-to-close + animated opacity)
    const backdrop = document.createElement('div');
    backdrop.setAttribute('data-backdrop', '');
    Object.assign(backdrop.style, {
      position: 'fixed', inset: '0',
      backgroundColor: 'rgba(0,0,0,0.3)',
      opacity: '0',
      transition: 'opacity 300ms ease-out',
    });

    // Panel
    const panel = document.createElement('div');
    panel.setAttribute('data-panel', '');
    if (theme) panel.classList.add(theme);
    const translateOut = side === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
    Object.assign(panel.style, {
      position: 'fixed', top: '0',
      [side === 'right' ? 'right' : 'left']: '0',
      height: '100%', width: '90vw', maxWidth: '30rem',
      background: 'var(--background)', color: 'var(--foreground)',
      display: 'flex', flexDirection: 'column',
      boxShadow: '0 20px 25px -5px rgba(0,0,0,.1), 0 8px 10px -6px rgba(0,0,0,.1)',
      transform: translateOut,
      transition: 'transform 300ms ease-out',
    });

    // Header with close button
    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
      padding: '1rem', flexShrink: '0',
    });

    const closeBtn = document.createElement('button');
    closeBtn.setAttribute('data-close', '');
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.setAttribute('type', 'button');
    Object.assign(closeBtn.style, {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '2rem', height: '2rem', borderRadius: '9999px',
      border: 'none', background: 'transparent', color: 'inherit',
      cursor: 'pointer', transition: 'background-color 150ms',
    });
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.backgroundColor = 'var(--warm-300, rgba(0,0,0,0.08))'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.backgroundColor = 'transparent'; });
    closeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    header.appendChild(closeBtn);

    // Body (scrollable)
    const body = document.createElement('div');
    body.setAttribute('data-body', '');
    Object.assign(body.style, {
      flex: '1', overflowY: 'auto', minHeight: '0',
      paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingBottom: '2rem',
    });
    body.innerHTML = originalContent;

    panel.appendChild(header);
    panel.appendChild(body);
    dialog.appendChild(backdrop);
    dialog.appendChild(panel);
    this.appendChild(dialog);

    // Cache refs
    this._dialog = dialog;
    this._backdrop = backdrop;
    this._panel = panel;
    this._side = side;
    this._translateOut = translateOut;

    // Events
    backdrop.addEventListener('click', this._onBackdropClick);
    closeBtn.addEventListener('click', this._onCloseClick);
    dialog.addEventListener('cancel', this._onDialogCancel);

    // Reveal — content is now inside a closed <dialog>, safe to show the host
    this.style.display = '';
  }

  disconnectedCallback() {
    if (this._backdrop) this._backdrop.removeEventListener('click', this._onBackdropClick);
    if (this._dialog) this._dialog.removeEventListener('cancel', this._onDialogCancel);
  }

  /** Open the drawer */
  showModal() {
    if (this._isOpen || !this._dialog) return;
    this._isOpen = true;
    this._closing = false;

    this._dialog.showModal();
    document.body.classList.add('overflow-hidden');

    // Animate in on next frame
    requestAnimationFrame(() => {
      this._backdrop.style.opacity = '1';
      this._panel.style.transform = 'translateX(0)';
    });
  }

  /** Close the drawer with animation */
  close() {
    if (!this._isOpen || this._closing) return;
    this._closing = true;

    // Speed up the leave animation
    this._panel.style.transition = 'transform 200ms ease-in';
    this._backdrop.style.opacity = '0';
    this._panel.style.transform = this._translateOut;

    this._panel.addEventListener('transitionend', this._onTransitionEnd, { once: true });

    // Safety timeout in case transitionend doesn't fire
    this._closeTimer = setTimeout(() => this._finishClose(), 250);
  }

  _onTransitionEnd() {
    this._finishClose();
  }

  _finishClose() {
    if (!this._closing) return;
    clearTimeout(this._closeTimer);
    this._isOpen = false;
    this._closing = false;

    document.body.classList.remove('overflow-hidden');
    this._dialog.close();

    // Reset for next open
    this._panel.style.transition = 'transform 300ms ease-out';
  }

  _onBackdropClick(e) {
    if (e.target.hasAttribute('data-backdrop')) this.close();
  }

  _onCloseClick() {
    this.close();
  }

  _onDialogCancel(e) {
    // Prevent native instant close; use our animated close instead
    e.preventDefault();
    this.close();
  }

  /** Replace the body content (used when edition changes via fetch) */
  updateBody(html) {
    const body = this.querySelector('[data-body]');
    if (body) body.innerHTML = html;
  }
}

if (!customElements.get('side-drawer')) {
  customElements.define('side-drawer', SideDrawer);
}

// Global delegated click handler for trigger buttons
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-open-drawer]');
  if (!trigger) return;
  const drawerId = trigger.getAttribute('data-open-drawer');
  const drawer = document.getElementById(drawerId);
  if (drawer && typeof drawer.showModal === 'function') {
    drawer.showModal();
  }
});
